import { getErrorMessage } from '@/api/errors'
import { MarkdownEditor } from '@/components/MarkdownEditor'
import { ProjectMemberPicker } from '@/components/projects/ProjectMemberPicker'
import { extractValidationMessage } from '@/lib/validationErrors'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCreateWorkItem } from '@/hooks'
import type { CreateWorkItemRequest } from '@/types'
import { Check } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

const TITLE_MAX = 256
const DESCRIPTION_MAX = 50000
const EPIC_MAX = 256
const FEATURE_MAX = 256
const TECHNICAL_APPROACH_MAX = 50000

interface CreateWorkItemDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateWorkItemDialog({ projectId, open, onOpenChange }: CreateWorkItemDialogProps) {
  const createWorkItem = useCreateWorkItem(projectId)
  const { register, handleSubmit, watch, reset, control, formState: { errors } } = useForm<CreateWorkItemRequest>({
    defaultValues: { title: '', description: '', technicalApproach: '', epic: '', feature: '', assignedToUserId: null },
  })

  const titleValue = watch('title') ?? ''
  const descriptionValue = watch('description') ?? ''
  const epicValue = watch('epic') ?? ''
  const featureValue = watch('feature') ?? ''
  const technicalApproachValue = watch('technicalApproach') ?? ''

  const onSubmit = async (data: CreateWorkItemRequest) => {
    try {
      const title = data.title.trim()
      const description = data.description.trim()
      const technicalApproach = data.technicalApproach?.trim()
      const epic = data.epic?.trim()
      const feature = data.feature?.trim()
      await createWorkItem.mutateAsync({
        title,
        description,
        assignedToUserId: data.assignedToUserId ?? null,
        ...(technicalApproach ? { technicalApproach } : {}),
        ...(epic ? { epic } : {}),
        ...(feature ? { feature } : {}),
      })
      toast.success('Work item created successfully')
      reset()
      onOpenChange(false)
    } catch (error) {
      toast.error(
        extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create work item',
      )
    }
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset()
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">Create Work Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto bg-input-background">
            <div className="max-w-4xl mx-auto py-8 px-6 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
                  <span className="text-xs text-muted-foreground">{titleValue.trim().length}/{TITLE_MAX}</span>
                </div>
                <Input
                  id="title"
                  placeholder="Enter work item title"
                  {...register('title', {
                    required: 'Work item title is required',
                    validate: (value) => {
                      const trimmed = value.trim()
                      if (trimmed.length === 0) return 'Work item title is required'
                      if (trimmed.length > TITLE_MAX) return `Title must be at most ${TITLE_MAX} characters`
                      return true
                    },
                  })}
                />
                {errors.title && (
                  <p className="text-sm text-destructive" role="alert">{errors.title.message}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="epic">Epic</Label>
                    <span className="text-xs text-muted-foreground">{epicValue.trim().length}/{EPIC_MAX}</span>
                  </div>
                  <Input
                    id="epic"
                    placeholder="Enter epic name"
                    {...register('epic', {
                      validate: (value) => {
                        if (value && value.trim().length > EPIC_MAX) return `Epic must be at most ${EPIC_MAX} characters`
                        return true
                      },
                    })}
                  />
                  {errors.epic && (
                    <p className="text-sm text-destructive" role="alert">{errors.epic.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="feature">Feature</Label>
                    <span className="text-xs text-muted-foreground">{featureValue.trim().length}/{FEATURE_MAX}</span>
                  </div>
                  <Input
                    id="feature"
                    placeholder="Enter feature name"
                    {...register('feature', {
                      validate: (value) => {
                        if (value && value.trim().length > FEATURE_MAX) return `Feature must be at most ${FEATURE_MAX} characters`
                        return true
                      },
                    })}
                  />
                  {errors.feature && (
                    <p className="text-sm text-destructive" role="alert">{errors.feature.message}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-assigned-to">Assign to</Label>
                  <Controller
                    name="assignedToUserId"
                    control={control}
                    render={({ field }) => (
                      <ProjectMemberPicker
                        projectId={projectId}
                        inputId="create-assigned-to"
                        value={field.value ?? null}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description">Description <span className="text-red-500">*</span></Label>
                  <span className="text-xs text-muted-foreground">{descriptionValue.trim().length}/{DESCRIPTION_MAX}</span>
                </div>
                <div className="h-[calc(100vh-360px)] flex flex-col">
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: 'Work item description is required',
                      validate: (value) => {
                        const trimmed = value.trim()
                        if (trimmed.length === 0) return 'Work item description is required'
                        if (trimmed.length > DESCRIPTION_MAX) return `Description must be at most ${DESCRIPTION_MAX} characters`
                        return true
                      },
                    }}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Enter work item description"
                        fillHeight
                      />
                    )}
                  />
                </div>
                {errors.description && (
                  <p className="text-sm text-destructive" role="alert">{errors.description.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="technicalApproach">Technical Approach</Label>
                  <span className="text-xs text-muted-foreground">{technicalApproachValue.trim().length}/{TECHNICAL_APPROACH_MAX}</span>
                </div>
                <div className="h-[calc(100vh-360px)] flex flex-col">
                  <Controller
                    name="technicalApproach"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (value && value.trim().length > TECHNICAL_APPROACH_MAX) return `Technical Approach must be at most ${TECHNICAL_APPROACH_MAX} characters`
                        return true
                      },
                    }}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder="Enter technical approach"
                        fillHeight
                      />
                    )}
                  />
                </div>
                {errors.technicalApproach && (
                  <p className="text-sm text-destructive" role="alert">{errors.technicalApproach.message}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="px-8 py-4 border-t shrink-0 bg-background">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createWorkItem.isPending}
            >
              <Check className="h-4 w-4" />
              {createWorkItem.isPending ? 'Creating...' : 'Create Work Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
